import React, { useState } from 'react';
import '../css/Program.css';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FaLeaf, 
  FaArrowRight, 
  FaCheckCircle,
  FaGem,
  FaStar,
  FaShoppingCart,
  FaInfoCircle,
  FaWeightHanging,
  FaLeaf as FaOrganic
} from 'react-icons/fa';
import { MdHealthAndSafety } from 'react-icons/md';

// Import your product images
import moringaImage from '../img/moringapowderimage.png';
import beetrootImage from '../img/beetrootpowderimage.png';
import garlicImage from '../img/garllicpowderimage.png';
import onionImage from "../img/Untitled design (7).png";

const productList = [
  {
    id: 1,
    title: 'Organic Moringa Powder',
    icon: <FaLeaf size={28} />,
    color: '#4CAF50',
    gradient: 'linear-gradient(135deg, #66BB6A, #2E7D32)',
    description: 'Pure, nutrient-dense Moringa powder packed with vitamins, minerals, and antioxidants.',
    benefits: ['Rich in antioxidants', 'Supports immune health', 'Natural energy booster', '100% pure & organic'],
    tag: 'Best Seller',
    image: moringaImage,
    weight: '200g',
    nutrition: {
      servingSize: '25g',
      calories: '100',
      protein: '0g',
      carbs: '0g',
      fat: '0g'
    }
  },
  {
    id: 2,
    title: 'Organic Beetroot Powder',
    icon: <FaLeaf size={28} />,
    color: '#EF5350',
    gradient: 'linear-gradient(135deg, #EF9A9A, #C62828)',
    description: 'Nutrient-rich Beetroot powder made from premium quality beets for athletic performance.',
    benefits: ['Improves circulation', 'Enhances performance', 'Rich in iron & folate', '100% natural'],
    tag: 'Popular',
    image: beetrootImage,
    weight: '200g',
    nutrition: {
      servingSize: '5g',
      calories: '15',
      protein: '0g',
      carbs: '0g',
      fat: '0g'
    }
  },
  {
    id: 3,
    title: 'Organic Garlic Powder',
    icon: <FaLeaf size={28} />,
    color: '#FFA726',
    gradient: 'linear-gradient(135deg, #FFD54F, #F57C00)',
    description: 'Premium quality Garlic powder with authentic flavor and maximum health benefits.',
    benefits: ['Supports heart health', 'Boosts immunity', 'Natural antibacterial', 'Rich in antioxidants'],
    tag: 'Premium',
    image: garlicImage,
    weight: '200g',
    nutrition: {
      servingSize: '5g',
      calories: '0',
      protein: '0g',
      carbs: '0g',
      fat: '0g'
    }
  },
  {
    id: 4,
    title: 'Organic Onion Powder',
    icon: <FaLeaf size={28} />,
    color: '#8D6E63',
    gradient: 'linear-gradient(135deg, #A1887F, #5D4037)',
    description: 'Premium quality Onion powder made from pure organic onions with rich flavor and nutritional benefits.',
    benefits: ['Rich in antioxidants', 'Supports digestive health', 'Boosts immunity', '100% natural & pure'],
    tag: 'New',
    image: onionImage,
    weight: '200g',
    nutrition: {
      servingSize: '5g',
      calories: '10',
      protein: '0g',
      carbs: '0g',
      fat: '0g'
    }
  }
];

function Programhome() {
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const navigate = useNavigate();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  // Split products into rows of 2 for all screen sizes
  const rows = [];
  for (let i = 0; i < productList.length; i += 2) {
    rows.push(productList.slice(i, i + 2));
  }

  const handleBuyNow = (product) => {
    // Navigate to /demo with product data
    navigate('/demo', { 
      state: { 
        product: product,
        from: 'program'
      } 
    });
  };

  return (
    <div className="tg-hero-wrapper">
      {/* Animated Background */}
      <div className="tg-bg-animated">
        <div className="tg-bg-blob tg-blob1"></div>
        <div className="tg-bg-blob tg-blob2"></div>
        <div className="tg-bg-blob tg-blob3"></div>
      </div>

      <div className="container py-5 position-relative" style={{ zIndex: 2 }}>
        {/* Header Section */}
        <motion.div 
          className="text-center mb-5"
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="tg-badge-wrap"
          >
            <span className="tg-premium-badge">
              <FaGem className="tg-badge-icon" /> Pure Organic Collection
            </span>
          </motion.div>

          <h1 className="tg-main-title">
            <span className="tg-title-green">Terra</span>
            <span className="tg-title-gold">Gold</span>
            <span className="tg-title-divider">|</span>
            <span className="tg-title-sub">Superfood Powders</span>
          </h1>

          <motion.div 
            className="tg-title-underline"
            initial={{ width: 0 }}
            animate={{ width: '80px' }}
            transition={{ delay: 0.4, duration: 0.6 }}
          />

          <motion.p 
            className="tg-subtitle"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.8 }}
          >
            Premium organic superfood powders crafted from nature's finest ingredients,
            <br className="d-none d-sm-block" />
            packed with essential nutrients for your daily wellness
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="tg-stats-row"
          >
            <div className="tg-stat-item">
              <span className="tg-stat-number">100%</span>
              <span className="tg-stat-label">Natural</span>
            </div>
            <div className="tg-stat-divider"></div>
            <div className="tg-stat-item">
              <span className="tg-stat-number">0%</span>
              <span className="tg-stat-label">Additives</span>
            </div>
            <div className="tg-stat-divider"></div>
            <div className="tg-stat-item">
              <span className="tg-stat-number">✓</span>
              <span className="tg-stat-label">Organic Certified</span>
            </div>
          </motion.div>
        </motion.div>

        {/* Products Grid - 2 columns on all screen sizes */}
        {rows.map((row, rowIndex) => (
          <div key={`row-${rowIndex}`} className="row g-4 mb-4 justify-content-center">
            {row.map((item) => (
              <motion.div
                key={item.id}
                className="col-6 col-lg-6"
                variants={itemVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                onHoverStart={() => setHoveredId(item.id)}
                onHoverEnd={() => setHoveredId(null)}
              >
                <TGProductCard 
                  item={item} 
                  isHovered={hoveredId === item.id}
                  isSelected={selectedId === item.id}
                  onSelect={() => setSelectedId(selectedId === item.id ? null : item.id)}
                  onBuyNow={handleBuyNow}
                />
              </motion.div>
            ))}
          </div>
        ))}

        {/* Call to Action */}
        <motion.div 
          className="text-center mt-5 pt-3"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          viewport={{ once: true }}
        >
          <Link to="/demo" style={{ textDecoration: 'none' }}>
            <motion.button 
              className="tg-explore-btn"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
            >
              View All Products <FaArrowRight className="tg-btn-icon" />
            </motion.button>
          </Link>
        </motion.div>
      </div>
    </div>
  );
}

const TGProductCard = ({ item, isHovered, isSelected, onSelect, onBuyNow }) => {
  return (
    <motion.div
      className={`tg-product-card ${isHovered ? 'tg-card-hovered' : ''} ${isSelected ? 'tg-card-selected' : ''}`}
      whileHover={{ 
        y: -10,
        transition: { duration: 0.3 }
      }}
      onClick={onSelect}
    >
      {/* Card Shadow */}
      <div className="tg-product-shadow"></div>
      
      {/* Tag Badge */}
      <div className="tg-product-tag" style={{ background: item.gradient }}>
        <FaStar className="tg-tag-icon" /> {item.tag}
      </div>

      {/* Product Image Section */}
      <div className="tg-product-image-wrapper">
        <motion.div 
          className="tg-product-image-container"
          whileHover={{ scale: 1.02 }}
          transition={{ type: "spring", stiffness: 300 }}
        >
          <img 
            src={item.image} 
            alt={item.title}
            className="tg-product-image"
          />
          
          {/* Floating badges on image */}
          <div className="tg-image-badges">
            <span className="tg-image-badge organic">
              <FaOrganic /> 100% Organic
            </span>
            <span className="tg-image-badge weight">
              <FaWeightHanging /> {item.weight}
            </span>
          </div>
        </motion.div>
      </div>

      {/* Product Info */}
      <div className="tg-product-content">
        <h3 className="tg-product-title">{item.title}</h3>
        
        <p className="tg-product-desc">{item.description}</p>

        {/* Quick Nutrition Info */}
        <div className="tg-nutrition-mini">
          <div className="tg-nutrition-item">
            <span className="tg-nutrition-label">Serving</span>
            <span className="tg-nutrition-value">{item.nutrition.servingSize}</span>
          </div>
          <div className="tg-nutrition-divider"></div>
          <div className="tg-nutrition-item">
            <span className="tg-nutrition-label">Calories</span>
            <span className="tg-nutrition-value">{item.nutrition.calories}</span>
          </div>
        </div>

        {/* Benefits - shown on hover or click */}
        <AnimatePresence>
          {(isHovered || isSelected) && (
            <motion.div 
              className="tg-product-benefits"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              {item.benefits.map((benefit, index) => (
                <motion.div 
                  key={index}
                  className="tg-benefit-item"
                  initial={{ x: -15, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ delay: index * 0.05 }}
                >
                  <FaCheckCircle className="tg-benefit-icon" style={{ color: item.color }} />
                  <span>{benefit}</span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Action Buttons */}
        <div className="tg-product-actions">
          <motion.button 
            className="tg-action-btn tg-btn-buy"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            style={{ background: item.gradient }}
            onClick={(e) => {
              e.stopPropagation();
              onBuyNow(item);
            }}
          >
            <FaShoppingCart /> Buy Now
          </motion.button>
          <motion.button 
            className="tg-action-btn tg-btn-info"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={(e) => e.stopPropagation()}
          >
            <FaInfoCircle />
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
};

export default Programhome;